import React from 'react';

/**
 * DashboardLayout wraps the dashboard page content.
 * It handles page padding, responsive grid gutters, and fallback skeleton display when loading data.
 * 
 * Props:
 * - title: optional page title used by headless header or SEO.
 * - isLoading: if true, render skeleton placeholders instead of children.
 * - children: the page content.
 */
export interface DashboardLayoutProps {
  title?: string;
  isLoading?: boolean;
  /** When true, uses reduced top padding for content-dense pages (e.g. posture analysis, drill library). */
  compact?: boolean;
  /** Drop the 1280px cap so the page fills the main column. */
  fullBleed?: boolean;
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ title, isLoading, compact, fullBleed, children }) => {
  // Optionally set document title
  React.useEffect(() => {
    if (title) {
      document.title = `${title} | harry`;
    }
  }, [title]);

  // Render skeleton when loading
  if (isLoading) {
    return (
      <div className="max-w-screen-xl mx-auto p-4 sm:p-6 md:p-8 lg:p-10 animate-pulse space-y-6">
        <div className="h-8 w-2/3 rounded bg-surface/50" />
        <div className="h-4 w-1/2 rounded bg-surface/50" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="h-32 rounded-lg bg-surface/50" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 h-64 rounded-lg bg-surface/50" />
          <div className="lg:col-span-5 h-64 rounded-lg bg-surface/50" />
        </div>
      </div>
    );
  }

  const paddingClass = fullBleed
    ? 'px-2 sm:px-3 pt-1 pb-2'
    : compact
      ? 'pt-1 sm:pt-3 md:pt-4 px-2 sm:px-6 md:px-8 lg:px-10 pb-0 sm:pb-6 md:pb-8 lg:pb-10'
      : 'p-4 sm:p-6 md:p-8 lg:p-10';
  const widthClass = fullBleed ? 'max-w-none h-full' : 'max-w-screen-xl';
  return (
    <div className={`${widthClass} mx-auto w-full ${(compact || fullBleed) ? 'flex flex-1 min-h-0 flex-col' : ''} ${paddingClass}`}>
      {children}
    </div>
  );
};

export default DashboardLayout;

